import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertCircle,
  Building2,
  ChevronDown,
  Columns,
  Download,
  Loader2,
  Maximize2,
  RefreshCw,
  Sparkles,
  Table2,
  User,
  X,
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import {
  addProspectingSavedListField,
  loadProspectingSavedList,
  loadProspectingSavedListCsv,
  runProspectingSavedListEnrichment,
  updateProspectingSavedListValue,
} from './prospectingApi';
import { buildBrandfetchLogoUrl } from './brandfetchLogo';

const MANUAL_COLUMN_PREFIX = 'manual:';
const EMPTY_ARRAY = [];
const DEFAULT_COLUMN_WIDTH_PX = 176;
const MIN_COLUMN_WIDTH_PX = 96;
const MAX_COLUMN_WIDTH_PX = 640;
const RECORD_DETAIL_MARKDOWN_PLUGINS = [remarkGfm];
const RECORD_DETAIL_MARKDOWN_COMPONENTS = {
  p({ children }) {
    return <p className="my-2 first:mt-0 last:mb-0 whitespace-pre-wrap">{children}</p>;
  },
  ul({ children }) {
    return <ul className="my-2 ml-1 list-disc list-outside space-y-1 pl-4 marker:text-gray-400 dark:marker:text-gray-500">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="my-2 ml-1 list-decimal list-outside space-y-1 pl-4 marker:text-gray-400 dark:marker:text-gray-500">{children}</ol>;
  },
  li({ children }) {
    return <li className="pl-0.5">{children}</li>;
  },
  strong({ children }) {
    return <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>;
  },
  em({ children }) {
    return <em className="italic text-gray-800 dark:text-gray-200">{children}</em>;
  },
  code({ children, ...props }) {
    const hasNewlines = String(children).includes('\n');
    const codeProps = markdownElementProps(props);

    if (hasNewlines) {
      return (
        <code
          className="my-2 block overflow-x-auto rounded-lg border border-gray-200/70 bg-white/70 px-2.5 py-2 text-xs leading-relaxed text-gray-800 dark:border-gray-700/70 dark:bg-gray-900/60 dark:text-gray-100"
          {...codeProps}
        >
          {children}
        </code>
      );
    }

    return (
      <code
        className="rounded-md border border-gray-200/70 bg-white/70 px-1 py-0.5 text-[0.8em] text-gray-800 dark:border-gray-700/70 dark:bg-gray-900/60 dark:text-gray-100"
        {...codeProps}
      >
        {children}
      </code>
    );
  },
  a({ children, href, ...props }) {
    const linkProps = markdownElementProps(props);
    return (
      <a
        {...linkProps}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 transition-colors hover:text-brand-800 hover:decoration-brand-500 dark:text-brand-300 dark:decoration-brand-700 dark:hover:text-brand-200"
      >
        {children}
      </a>
    );
  },
};
const ENRICHMENT_FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
];

const ENRICHMENT_PRESETS = {
  person: [
    {
      key: 'pain-points',
      label: 'Pain points',
      fieldType: 'text',
      prompt: 'List the top 2 pain points this person likely has in their role.',
    },
    {
      key: 'cold-email-opener',
      label: 'Cold email opener',
      fieldType: 'text',
      prompt: 'Write a personalized cold email opener in under 20 words.',
    },
    {
      key: 'fit-score',
      label: 'Fit score',
      fieldType: 'number',
      prompt: 'Return an ICP fit score from 0 to 100 as a number only.',
    },
    {
      key: 'intro-angle',
      label: 'Intro angle',
      fieldType: 'text',
      prompt: 'Suggest one short intro angle we can use for outreach.',
    },
  ],
  company: [
    {
      key: 'pain-points',
      label: 'Pain points',
      fieldType: 'text',
      prompt: 'List the top 2 business pain points this company likely has.',
    },
    {
      key: 'fit-score',
      label: 'Fit score',
      fieldType: 'number',
      prompt: 'Return an ICP fit score from 0 to 100 as a number only.',
    },
    {
      key: 'buying-signal',
      label: 'Buying signal',
      fieldType: 'text',
      prompt: 'Give one likely buying signal for this company in one sentence.',
    },
    {
      key: 'outreach-thesis',
      label: 'Outreach thesis',
      fieldType: 'text',
      prompt: 'Write one short reason this company is worth targeting.',
    },
  ],
};

function formatLocation(location = {}) {
  return [location.city, location.region, location.country_code]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
}

function markdownElementProps(props) {
  const elementProps = { ...props };
  delete elementProps.node;
  return elementProps;
}

function RecordDetailMarkdown({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={RECORD_DETAIL_MARKDOWN_PLUGINS}
      components={RECORD_DETAIL_MARKDOWN_COMPONENTS}
      skipHtml
    >
      {content}
    </ReactMarkdown>
  );
}

function valueToDraft(value) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function safeFilename(name) {
  const slug = String(name || 'prospecting-list')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'prospecting-list'}.csv`;
}

function getCompanySnapshot(record) {
  const snapshot = record?.snapshot || {};
  return {
    snapshot,
    company: snapshot.company || {},
    fields: snapshot.fields || {},
    metrics: snapshot.metrics || {},
  };
}

function getCompanyName(record) {
  const { snapshot, company, fields } = getCompanySnapshot(record);
  if (snapshot.entity_type === 'company') return snapshot.display_name;
  return company.display_name || fields.company_name || '';
}

function companyIndustry(record) {
  const { company, fields } = getCompanySnapshot(record);
  return company.industry || fields.company_industry || fields.industry || '';
}

function companyDomain(record) {
  const { company, fields } = getCompanySnapshot(record);
  return company.domain || fields.company_domain || '';
}

function companyWebsiteForLogo(record) {
  const { snapshot, company, fields } = getCompanySnapshot(record);
  return (
    snapshot?.links?.website_url
    || fields.website_url
    || fields.website
    || company.domain
    || fields.company_domain
    || fields.company_website
    || fields.domain
    || ''
  );
}

function companyLogoUrl(record) {
  return buildBrandfetchLogoUrl(companyWebsiteForLogo(record));
}

function companyPhone(record) {
  const { snapshot, fields } = getCompanySnapshot(record);
  return (
    snapshot?.links?.phone
    || snapshot?.links?.phone_number
    || snapshot?.links?.formatted_phone_number
    || fields.company_phone
    || fields.phone
    || fields.phone_number
    || fields.formatted_phone_number
    || fields.international_phone_number
    || ''
  );
}

function companyEmployeeRange(record) {
  const { company, fields } = getCompanySnapshot(record);
  return company.employee_count_range || fields.company_employee_count_range || fields.employee_count_range || '';
}

function companyEmployeeCount(record) {
  const { company, fields, metrics } = getCompanySnapshot(record);
  return company.employee_count || metrics.employee_count || fields.employee_count || '';
}

export function humanizeFieldKey(value) {
  const input = String(value || '').trim();
  if (!input) return '';
  return input
    .replace(/[._-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function normalizeUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const hasExplicitScheme = /^[a-z][a-z\d+\-.]*:/i.test(raw);
  const normalized = hasExplicitScheme ? raw : `https://${raw}`;
  try {
    const parsed = new URL(normalized);
    const protocol = parsed.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') return '';
    return parsed.toString();
  } catch {
    return '';
  }
}

function formatCellValue(value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    return value.map((item) => formatCellValue(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function compactUrl(url) {
  return String(url || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function availabilityTone(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'verified' || normalized === 'present') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300';
  }
  if (normalized === 'enrichable') {
    return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300';
  }
  if (normalized === 'suppressed') {
    return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300';
  }
  return 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

function collectSnapshotKeys(records = []) {
  const metricKeys = new Set();
  const fieldKeys = new Set();

  for (const record of records) {
    const snapshot = record?.snapshot || {};
    Object.keys(snapshot.metrics || {}).forEach((key) => metricKeys.add(key));
    Object.keys(snapshot.fields || {}).forEach((key) => fieldKeys.add(key));
  }

  return {
    metricKeys: Array.from(metricKeys).sort((a, b) => a.localeCompare(b)),
    fieldKeys: Array.from(fieldKeys).sort((a, b) => a.localeCompare(b)),
  };
}

export function buildDefaultVisibilityMap(columns = []) {
  const visibility = {};
  columns.forEach((column) => {
    visibility[column.id] = column.defaultVisible !== false;
  });
  return visibility;
}

export function mergeColumnVisibility(currentVisibility = {}, columns = []) {
  const defaults = buildDefaultVisibilityMap(columns);
  const merged = { ...defaults };

  columns.forEach((column) => {
    if (currentVisibility[column.id] !== undefined) {
      merged[column.id] = currentVisibility[column.id];
    }
  });

  return merged;
}

function clampColumnWidth(value) {
  if (!Number.isFinite(value)) return DEFAULT_COLUMN_WIDTH_PX;
  return Math.max(MIN_COLUMN_WIDTH_PX, Math.min(MAX_COLUMN_WIDTH_PX, Math.round(value)));
}

function parseTailwindScaleWidthPx(widthClass = '') {
  const wMatch = String(widthClass).match(/\bw-(\d+)\b/);
  if (wMatch) return Number(wMatch[1]) * 4;

  const arbitraryWMatch = String(widthClass).match(/\bw-\[(\d+(?:\.\d+)?)rem\]/);
  if (arbitraryWMatch) return Number(arbitraryWMatch[1]) * 16;

  const minWMatch = String(widthClass).match(/\bmin-w-\[(\d+(?:\.\d+)?)rem\]/);
  if (minWMatch) return Number(minWMatch[1]) * 16;

  return null;
}

function getDefaultColumnWidthPx(column) {
  const parsed = parseTailwindScaleWidthPx(column?.widthClass || '');
  return clampColumnWidth(parsed ?? DEFAULT_COLUMN_WIDTH_PX);
}

function buildDefaultColumnWidthMap(columns = []) {
  const widthMap = {};
  columns.forEach((column) => {
    widthMap[column.id] = getDefaultColumnWidthPx(column);
  });
  return widthMap;
}

function mergeColumnWidths(currentWidths = {}, columns = []) {
  const defaults = buildDefaultColumnWidthMap(columns);
  const merged = { ...defaults };

  columns.forEach((column) => {
    if (currentWidths[column.id] !== undefined) {
      merged[column.id] = clampColumnWidth(currentWidths[column.id]);
    }
  });

  return merged;
}

export function buildBaseColumns(detail) {
  const records = detail?.records || [];
  const inferredEntityType = detail?.entity_type || records[0]?.snapshot?.entity_type || '';
  const isCompanyList = inferredEntityType === 'company';
  const { metricKeys, fieldKeys } = collectSnapshotKeys(records);

  const fixedColumns = [
    {
      id: 'base:name',
      label: 'Name',
      group: 'Identity',
      defaultVisible: true,
      widthClass: 'min-w-[14rem] w-56',
      getValue: (record) => record?.snapshot?.display_name || '',
    },
    {
      id: 'base:subtitle',
      label: 'Subtitle',
      group: 'Identity',
      defaultVisible: true,
      widthClass: 'min-w-[11rem] w-44',
      getValue: (record) => record?.snapshot?.subtitle || record?.snapshot?.fields?.title || '',
    },
    {
      id: 'base:entity_id',
      label: 'Entity ID',
      group: 'Identity',
      defaultVisible: false,
      widthClass: 'min-w-[10rem] w-40',
      getValue: (record) => record?.entity_id || '',
    },
    {
      id: 'base:entity_type',
      label: 'Entity Type',
      group: 'Identity',
      defaultVisible: false,
      widthClass: 'min-w-[9rem] w-36',
      getValue: (record) => humanizeFieldKey(record?.snapshot?.entity_type || ''),
    },
    {
      id: 'base:company',
      label: 'Company',
      group: 'Company',
      defaultVisible: true,
      widthClass: 'min-w-[12rem] w-48',
      getValue: (record) => getCompanyName(record),
    },
    {
      id: 'base:company_industry',
      label: 'Company Industry',
      group: 'Company',
      defaultVisible: false,
      widthClass: 'min-w-[10rem] w-40',
      getValue: (record) => companyIndustry(record),
    },
    {
      id: 'base:company_domain',
      label: 'Company Domain',
      group: 'Company',
      defaultVisible: false,
      widthClass: 'min-w-[10rem] w-40',
      getValue: (record) => companyDomain(record),
      valueType: 'url',
    },
    {
      id: 'base:company_employee_range',
      label: 'Company Size',
      group: 'Company',
      defaultVisible: false,
      widthClass: 'min-w-[9rem] w-36',
      getValue: (record) => companyEmployeeRange(record),
    },
    {
      id: 'base:company_employee_count',
      label: 'Employee Count',
      group: 'Company',
      defaultVisible: false,
      widthClass: 'min-w-[9rem] w-36',
      getValue: (record) => companyEmployeeCount(record),
    },
    {
      id: 'base:location',
      label: 'Location',
      group: 'Location',
      defaultVisible: true,
      widthClass: 'min-w-[10rem] w-40',
      getValue: (record) => formatLocation(record?.snapshot?.location),
    },
    {
      id: 'base:city',
      label: 'City',
      group: 'Location',
      defaultVisible: isCompanyList,
      widthClass: 'min-w-[9rem] w-36',
      getValue: (record) => record?.snapshot?.location?.city || '',
    },
    {
      id: 'base:region',
      label: 'Region',
      group: 'Location',
      defaultVisible: false,
      widthClass: 'min-w-[9rem] w-36',
      getValue: (record) => record?.snapshot?.location?.region || '',
    },
    {
      id: 'base:country_code',
      label: 'Country Code',
      group: 'Location',
      defaultVisible: false,
      widthClass: 'min-w-[8rem] w-32',
      getValue: (record) => record?.snapshot?.location?.country_code || '',
    },
    {
      id: 'base:linkedin_url',
      label: 'LinkedIn URL',
      group: 'Links',
      defaultVisible: false,
      widthClass: 'min-w-[11rem] w-44',
      getValue: (record) => record?.snapshot?.links?.linkedin_url || '',
      valueType: 'url',
    },
    {
      id: 'base:website_url',
      label: 'Website URL',
      group: 'Links',
      defaultVisible: false,
      widthClass: 'min-w-[11rem] w-44',
      getValue: (record) => record?.snapshot?.links?.website_url || '',
      valueType: 'url',
    },
    {
      id: 'base:phone',
      label: 'Phone',
      group: 'Links',
      defaultVisible: isCompanyList,
      widthClass: 'min-w-[10rem] w-40',
      getValue: (record) => companyPhone(record),
    },
    {
      id: 'base:email_status',
      label: 'Email Status',
      group: 'Availability',
      defaultVisible: false,
      widthClass: 'min-w-[8rem] w-32',
      getValue: (record) => record?.snapshot?.availability?.email || '',
      valueType: 'availability',
    },
    {
      id: 'base:phone_status',
      label: 'Phone Status',
      group: 'Availability',
      defaultVisible: false,
      widthClass: 'min-w-[8rem] w-32',
      getValue: (record) => record?.snapshot?.availability?.phone || '',
      valueType: 'availability',
    },
    {
      id: 'base:highlights',
      label: 'Highlights',
      group: 'Signals',
      defaultVisible: false,
      widthClass: 'min-w-[11rem] w-44',
      getValue: (record) => record?.snapshot?.highlights || [],
    },
  ];

  const metricColumns = metricKeys.map((key) => ({
    id: `base:metric:${key}`,
    label: `Metric · ${humanizeFieldKey(key)}`,
    group: 'Metrics',
    defaultVisible: false,
    widthClass: 'min-w-[9rem] w-36',
    getValue: (record) => record?.snapshot?.metrics?.[key],
  }));

  const sourceFieldColumns = fieldKeys.map((key) => ({
    id: `base:field:${key}`,
    label: `Field · ${humanizeFieldKey(key)}`,
    group: 'Source Fields',
    defaultVisible: false,
    widthClass: 'min-w-[10rem] w-40',
    getValue: (record) => record?.snapshot?.fields?.[key],
  }));

  return [...fixedColumns, ...metricColumns, ...sourceFieldColumns];
}

function StateMessage({ icon: Icon, title, children, action }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-white dark:bg-gray-900">
      <div className="max-w-sm text-center px-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 mb-3">
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          {title}
        </div>
        {children && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            {children}
          </p>
        )}
        {action}
      </div>
    </div>
  );
}

function CompanyCell({ record }) {
  const companyName = getCompanyName(record);

  if (!companyName) {
    return <div className="max-w-[200px] truncate" />;
  }

  return (
    <div className="max-w-[180px] truncate text-[13px] text-gray-600 dark:text-gray-300">
      {companyName}
    </div>
  );
}

function PendingAiCell({ value }) {
  const displayValue = valueToDraft(value).trim();

  if (displayValue) {
    return (
      <div className="min-w-[150px] rounded-md bg-brand-50/50 px-1.5 py-1 text-[11px] leading-4 text-brand-900 dark:bg-brand-900/20 dark:text-brand-100">
        {displayValue}
      </div>
    );
  }

  return (
    <div className="inline-flex min-h-7 min-w-[150px] items-center gap-1 rounded-md border border-dashed border-brand-200 bg-brand-50/70 px-1.5 py-1 text-brand-700 dark:border-brand-800 dark:bg-brand-900/20 dark:text-brand-200">
      <div className="inline-flex items-center gap-1">
        <Sparkles className="h-3 w-3" />
        <span className="text-[11px] font-medium animate-pulse">Generating</span>
      </div>
    </div>
  );
}

function AiValueCell({
  cell,
}) {
  const status = cell?.status || 'pending';
  if (status === 'pending' || status === 'running') {
    return <PendingAiCell value={cell?.value} />;
  }

  if (status === 'failed') {
    return (
      <div className="min-w-[150px] rounded-md border border-rose-200 bg-rose-50 px-1.5 py-1 text-[11px] text-rose-700 dark:border-rose-800 dark:bg-rose-950/20 dark:text-rose-300">
        {cell?.error || 'Enrichment failed'}
      </div>
    );
  }

  return <ReadOnlyCell value={cell?.value} />;
}

function EditableManualCell({ value, disabled, onCommit }) {
  const sourceValue = valueToDraft(value);
  const [draft, setDraft] = useState(sourceValue);

  useEffect(() => {
    setDraft(sourceValue);
  }, [sourceValue]);

  const commit = useCallback(() => {
    const next = draft.trim();
    if (next === sourceValue) return;
    onCommit(next);
  }, [draft, onCommit, sourceValue]);

  return (
    <input
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setDraft(sourceValue);
          event.currentTarget.blur();
        }
      }}
      className="w-full min-w-[140px] rounded-md border border-transparent bg-transparent px-1.5 py-1 text-[12px] text-gray-700 dark:text-gray-200 outline-none transition-colors hover:border-gray-200 hover:bg-white dark:hover:border-gray-700 dark:hover:bg-gray-900 focus:border-brand-300 focus:bg-white focus:ring-2 focus:ring-brand-100 dark:focus:border-brand-700 dark:focus:bg-gray-900 dark:focus:ring-brand-900/30 disabled:opacity-60"
    />
  );
}

function NameCell({ record }) {
  const snapshot = record?.snapshot || {};
  const isCompany = snapshot.entity_type === 'company';
  const Icon = isCompany ? Building2 : User;
  const logoUrl = isCompany ? companyLogoUrl(record) : null;
  const [showLogo, setShowLogo] = useState(Boolean(logoUrl));

  useEffect(() => {
    setShowLogo(Boolean(logoUrl));
  }, [logoUrl]);

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        {showLogo ? (
          <img
            src={logoUrl}
            alt={`${snapshot.display_name} logo`}
            onError={() => setShowLogo(false)}
            className="h-6 w-6 flex-shrink-0 rounded-md bg-white object-contain p-1 shadow-sm"
          />
        ) : (
          <div className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center ${isCompany ? 'rounded-md' : 'rounded-full'} bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300`}>
            <Icon className="h-3 w-3" />
          </div>
        )}
        <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-white" title={snapshot.display_name}>
          {snapshot.display_name}
        </span>
      </div>
    </div>
  );
}

function UrlValueCell({ value }) {
  const url = normalizeUrl(value);
  if (!url) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">-</span>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-block max-w-[220px] truncate text-[11px] text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-brand-600 hover:decoration-brand-400 dark:text-gray-300 dark:decoration-gray-600 dark:hover:text-brand-300"
      title={url}
    >
      {compactUrl(url)}
    </a>
  );
}

function AvailabilityCell({ value }) {
  const text = String(value || '').trim();
  if (!text) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">-</span>;
  }

  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${availabilityTone(text)}`}>
      {humanizeFieldKey(text)}
    </span>
  );
}

function ReadOnlyCell({ value }) {
  const text = formatCellValue(value).trim();
  if (!text) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">-</span>;
  }

  return (
    <div
      className="max-w-[260px] overflow-hidden text-[13px] leading-5 text-gray-700 dark:text-gray-200 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
      title={text}
    >
      {text}
    </div>
  );
}

function getRecordDetailValue(column, record) {
  if (!column || !record) return '';

  if (column.kind === 'manual') {
    const fieldId = column?.field?.id;
    if (!fieldId) return '';
    if (column.isAiColumn) {
      const cell = record.field_values?.[fieldId];
      const status = cell?.status || 'pending';
      if (status === 'failed') {
        return cell?.error ? `Failed: ${cell.error}` : 'Enrichment failed';
      }
      if (status === 'pending' || status === 'running') {
        const draft = formatCellValue(cell?.value).trim();
        return draft || 'Generating...';
      }
      return formatCellValue(cell?.value).trim();
    }
    return formatCellValue(record.manual_values?.[fieldId]).trim();
  }

  const value = column.getValue ? column.getValue(record) : '';
  return formatCellValue(value).trim();
}

function RecordDetailModal({
  open,
  record,
  columns,
  onClose,
}) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  const rows = useMemo(() => {
    if (!record) return [];
    return columns.map((column) => ({
      id: column.id,
      label: column.label,
      isAiColumn: Boolean(column.isAiColumn),
      isManualColumn: column.kind === 'manual',
      valueType: column.valueType || null,
      value: getRecordDetailValue(column, record),
    }));
  }, [columns, record]);

  if (!open || !record) return null;

  const name = record?.snapshot?.display_name || 'Record details';
  const subtitle = record?.snapshot?.subtitle || record?.snapshot?.fields?.title || '';
  const companyName = getCompanyName(record);
  const aiRows = rows.filter((row) => row.isAiColumn);
  const coreRows = rows.filter((row) => !row.isManualColumn);
  const customRows = rows.filter((row) => row.isManualColumn && !row.isAiColumn);

  const renderRowValue = (row, longForm = false) => {
    const textValue = String(row?.value || '').trim();
    const normalizedUrl = row?.valueType === 'url' ? normalizeUrl(textValue) : '';

    if (normalizedUrl) {
      return (
        <a
          href={normalizedUrl}
          target="_blank"
          rel="noreferrer"
          className="break-all text-brand-600 underline decoration-brand-300 underline-offset-2 hover:text-brand-700 dark:text-brand-300 dark:decoration-brand-700"
        >
          {normalizedUrl}
        </a>
      );
    }

    if (!textValue) {
      return <span className="text-gray-400 dark:text-gray-500">-</span>;
    }

    if (longForm) {
      return (
        <div className="break-words text-[15px] leading-7 text-gray-800 dark:text-gray-100">
          <RecordDetailMarkdown content={textValue} />
        </div>
      );
    }

    return (
      <div className="whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-200">
        {textValue}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[95]">
      <div className="absolute inset-0 bg-gray-950/45 backdrop-blur-[1px]" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="w-full max-w-6xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        >
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 dark:border-gray-700">
            <div className="min-w-0">
              <h3 className="truncate text-xl font-semibold text-gray-900 dark:text-white" title={name}>
                {name}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                {subtitle ? <span>{subtitle}</span> : null}
                {subtitle && companyName ? <span aria-hidden>·</span> : null}
                {companyName ? <span>{companyName}</span> : null}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition-colors hover:text-gray-700 dark:border-gray-700 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[74vh] overflow-y-auto px-6 py-6">
            <article className="mx-auto w-full max-w-4xl">
              <section>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  AI Enrichment
                </div>
                {aiRows.length === 0 ? (
                  <div className="mt-3 text-sm text-gray-400 dark:text-gray-500">
                    No AI enrichment fields on this record yet.
                  </div>
                ) : (
                  <div className="mt-3 divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-800 dark:border-gray-800">
                    {aiRows.map((row) => (
                      <article key={row.id} className="py-4">
                        <h4 className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.1em] text-brand-600 dark:text-brand-300">
                          <Sparkles className="h-3.5 w-3.5" />
                          {row.label}
                        </h4>
                        <div className="mt-2">
                          {renderRowValue(row, true)}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section className="mt-8">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  Core Data
                </div>
                <dl className="mt-3 divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-800 dark:border-gray-800">
                  {coreRows.map((row) => (
                    <div key={row.id} className="grid gap-1 py-2.5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                        {row.label}
                      </dt>
                      <dd className="min-w-0">
                        {renderRowValue(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              {customRows.length > 0 ? (
                <section className="mt-8">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                    Custom Fields
                  </div>
                  <dl className="mt-3 divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-800 dark:border-gray-800">
                    {customRows.map((row) => (
                      <div key={row.id} className="grid gap-1 py-2.5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4">
                        <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                          {row.label}
                        </dt>
                        <dd className="min-w-0">
                          {renderRowValue(row)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ) : null}
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColumnToggleItem({ column, checked, onToggle }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(column.id)}
        className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      <span className="min-w-0 truncate" title={column.label}>{column.label}</span>
    </label>
  );
}

function ColumnVisibilityMenu({
  baseColumns,
  manualColumns,
  columnVisibility,
  onToggle,
  onShowAllBase,
  onResetDefaults,
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const groupedBaseColumns = useMemo(() => {
    const groups = new Map();

    baseColumns.forEach((column) => {
      const groupKey = column.group || 'Other';
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(column);
    });

    return Array.from(groups.entries());
  }, [baseColumns]);

  const visibleCount = useMemo(() => (
    [...baseColumns, ...manualColumns].filter((column) => columnVisibility[column.id] !== false).length
  ), [baseColumns, manualColumns, columnVisibility]);

  return (
    <div ref={menuRef} className="relative z-50">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-brand-300 hover:text-brand-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-brand-700 dark:hover:text-brand-300"
      >
        <Columns className="h-3.5 w-3.5" />
        Columns
        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-300">
          {visibleCount}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>

      {open && (
        <>
          <div className="absolute right-0 z-[60] mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="border-b border-gray-100 px-3 py-2.5 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-900 dark:text-white">Visible columns</div>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={onShowAllBase}
                  className="text-[11px] font-medium text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-300"
                >
                  Show all base fields
                </button>
                <span className="text-[11px] text-gray-300 dark:text-gray-600" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  onClick={onResetDefaults}
                  className="text-[11px] font-medium text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-300"
                >
                  Reset defaults
                </button>
              </div>
            </div>
            <div className="max-h-[26rem] overflow-y-auto px-2 py-2">
              {groupedBaseColumns.map(([group, columns]) => (
                <div key={group} className="mb-2 last:mb-0">
                  <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {group}
                  </div>
                  <div className="space-y-0.5">
                    {columns.map((column) => (
                      <ColumnToggleItem
                        key={column.id}
                        column={column}
                        checked={columnVisibility[column.id] !== false}
                        onToggle={onToggle}
                      />
                    ))}
                  </div>
                </div>
              ))}

              <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
                <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Custom columns
                </div>
                {manualColumns.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-gray-400 dark:text-gray-500">
                    No custom columns yet
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {manualColumns.map((column) => (
                      <ColumnToggleItem
                        key={column.id}
                        column={column}
                        checked={columnVisibility[column.id] !== false}
                        onToggle={onToggle}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AddEnrichmentModal({
  open,
  entityType,
  creating = false,
  onClose,
  onCreate,
}) {
  const modalRef = useRef(null);
  const presets = useMemo(
    () => ENRICHMENT_PRESETS[entityType === 'company' ? 'company' : 'person'],
    [entityType]
  );
  const [selectedPresetKey, setSelectedPresetKey] = useState('custom');
  const [columnName, setColumnName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [fieldType, setFieldType] = useState('text');

  useEffect(() => {
    if (!open) return;
    setSelectedPresetKey('custom');
    setColumnName('');
    setPrompt('');
    setFieldType('text');
  }, [open, entityType]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const applyPreset = useCallback((preset) => {
    if (!preset) {
      setSelectedPresetKey('custom');
      return;
    }
    setSelectedPresetKey(preset.key);
    setColumnName(preset.label);
    setPrompt(preset.prompt);
    setFieldType(preset.fieldType);
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    const label = columnName.trim();
    const nextPrompt = prompt.trim();
    if (!label || !nextPrompt || creating) return;
    onCreate({
      label,
      prompt: nextPrompt,
      fieldType,
    });
  };

  if (!open) return null;

  const canCreate = Boolean(columnName.trim() && prompt.trim()) && !creating;

  return (
    <div className="fixed inset-0 z-[90]">
      <div className="absolute inset-0 bg-gray-950/40 backdrop-blur-[1px]" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        >
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 dark:border-gray-700">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Add enrichment
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Create an AI enrichment column by writing the prompt once.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition-colors hover:text-gray-700 dark:border-gray-700 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid gap-0 md:grid-cols-[220px_1fr]">
            <div className="border-r border-gray-100 px-4 py-4 dark:border-gray-700">
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                Suggestions
              </div>
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => applyPreset(null)}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                    selectedPresetKey === 'custom'
                      ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/70'
                  }`}
                >
                  Custom
                </button>
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                      selectedPresetKey === preset.key
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                        : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/70'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-900 dark:text-white">
                    Column name
                  </label>
                  <input
                    value={columnName}
                    onChange={(event) => setColumnName(event.target.value)}
                    placeholder="e.g. Pain points"
                    className="mt-1.5 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-900/40"
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    Data type
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {ENRICHMENT_FIELD_TYPES.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFieldType(option.value)}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                          fieldType === option.value
                            ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-900 dark:text-white">
                    Instructions
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Describe exactly what data Able should generate for every row."
                    rows={5}
                    className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-900/40"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canCreate}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Add enrichment
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function ProspectingSavedListTable({
  listId,
  reloadToken = 0,
  onReloadSavedLists,
}) {
  const { showError, showSuccess } = useToast();
  const [state, setState] = useState({
    loading: true,
    error: null,
    detail: null,
  });
  const [creatingEnrichment, setCreatingEnrichment] = useState(false);
  const [enrichmentModalOpen, setEnrichmentModalOpen] = useState(false);
  const [expandedRecordId, setExpandedRecordId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [savingCell, setSavingCell] = useState(null);
  const [runningEnrichmentFieldIds, setRunningEnrichmentFieldIds] = useState({});
  const [columnVisibilityState, setColumnVisibilityState] = useState({
    listId: null,
    map: {},
  });
  const [columnWidthState, setColumnWidthState] = useState({
    listId: null,
    map: {},
  });

  const loadDetail = useCallback((signal) => {
    return loadProspectingSavedList(listId, { signal })
      .then((detail) => {
        setState({ loading: false, error: null, detail });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ loading: false, error: error.message, detail: null });
      });
  }, [listId]);

  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, error: null, detail: null });

    loadDetail(controller.signal);

    return () => controller.abort();
  }, [listId, reloadToken, loadDetail]);

  const detail = state.detail;
  const records = detail?.records ?? EMPTY_ARRAY;
  const fields = detail?.fields ?? EMPTY_ARRAY;
  const listTitle = detail?.name || 'Saved list';
  const entityType = detail?.entity_type || 'person';
  const enrichmentJobs = detail?.enrichment_jobs || EMPTY_ARRAY;

  const baseColumns = useMemo(
    () => buildBaseColumns(detail),
    [detail]
  );

  const manualColumns = useMemo(
    () => fields.map((field) => {
      const isAiColumn = field.kind === 'ai_enriched';
      return {
        id: `${MANUAL_COLUMN_PREFIX}${field.id}`,
        kind: 'manual',
        label: field.label,
        field,
        isAiColumn,
        prompt: field.prompt || null,
        defaultVisible: true,
        widthClass: 'min-w-[11rem] w-44',
      };
    }),
    [fields]
  );

  const allColumns = useMemo(
    () => [...baseColumns, ...manualColumns],
    [baseColumns, manualColumns]
  );

  useEffect(() => {
    if (allColumns.length === 0) return;
    setColumnVisibilityState((current) => {
      const mapForList = current.listId === listId
        ? current.map
        : buildDefaultVisibilityMap(allColumns);
      const next = mergeColumnVisibility(mapForList, allColumns);
      return { listId, map: next };
    });
  }, [allColumns, listId]);

  useEffect(() => {
    if (allColumns.length === 0) return;
    setColumnWidthState((current) => {
      const mapForList = current.listId === listId
        ? current.map
        : buildDefaultColumnWidthMap(allColumns);
      const next = mergeColumnWidths(mapForList, allColumns);
      return { listId, map: next };
    });
  }, [allColumns, listId]);

  useEffect(() => {
    setRunningEnrichmentFieldIds({});
    setEnrichmentModalOpen(false);
    setExpandedRecordId(null);
  }, [listId]);

  const effectiveColumnVisibility = useMemo(() => {
    const mapForList = columnVisibilityState.listId === listId
      ? columnVisibilityState.map
      : {};
    return mergeColumnVisibility(mapForList, allColumns);
  }, [allColumns, columnVisibilityState, listId]);

  const effectiveColumnWidths = useMemo(() => {
    const mapForList = columnWidthState.listId === listId
      ? columnWidthState.map
      : {};
    return mergeColumnWidths(mapForList, allColumns);
  }, [allColumns, columnWidthState, listId]);

  const visibleColumns = useMemo(
    () => allColumns.filter((column) => effectiveColumnVisibility[column.id] !== false),
    [allColumns, effectiveColumnVisibility]
  );
  const hasActiveEnrichmentJobs = useMemo(
    () => enrichmentJobs.some((job) => job.status === 'queued' || job.status === 'running'),
    [enrichmentJobs]
  );
  const latestEnrichmentJobByField = useMemo(() => {
    const map = {};
    enrichmentJobs.forEach((job) => {
      if (!map[job.field_id]) {
        map[job.field_id] = job;
      }
    });
    return map;
  }, [enrichmentJobs]);

  useEffect(() => {
    if (!hasActiveEnrichmentJobs) return undefined;
    let activeController = null;
    const timer = window.setInterval(() => {
      if (activeController) {
        activeController.abort();
      }
      activeController = new AbortController();
      loadDetail(activeController.signal);
    }, 2500);
    return () => {
      window.clearInterval(timer);
      if (activeController) {
        activeController.abort();
      }
    };
  }, [hasActiveEnrichmentJobs, loadDetail]);

  const updateColumnVisibility = useCallback((updater) => {
    setColumnVisibilityState((current) => {
      const baseMap = current.listId === listId
        ? current.map
        : buildDefaultVisibilityMap(allColumns);
      const nextMap = updater(baseMap);
      return {
        listId,
        map: mergeColumnVisibility(nextMap, allColumns),
      };
    });
  }, [allColumns, listId]);

  const toggleColumn = useCallback((columnId) => {
    updateColumnVisibility((current) => ({
      ...current,
      [columnId]: !(current[columnId] !== false),
    }));
  }, [updateColumnVisibility]);

  const showAllBaseColumns = useCallback(() => {
    updateColumnVisibility((current) => {
      const next = { ...current };
      baseColumns.forEach((column) => {
        next[column.id] = true;
      });
      return next;
    });
  }, [baseColumns, updateColumnVisibility]);

  const resetDefaultColumns = useCallback(() => {
    setColumnVisibilityState({
      listId,
      map: buildDefaultVisibilityMap(allColumns),
    });
  }, [allColumns, listId]);

  const updateColumnWidths = useCallback((updater) => {
    setColumnWidthState((current) => {
      const baseMap = current.listId === listId
        ? current.map
        : buildDefaultColumnWidthMap(allColumns);
      const nextMap = updater(baseMap);
      return {
        listId,
        map: mergeColumnWidths(nextMap, allColumns),
      };
    });
  }, [allColumns, listId]);

  const startColumnResize = useCallback((event, columnId) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = effectiveColumnWidths[columnId] || getDefaultColumnWidthPx(
      allColumns.find((column) => column.id === columnId)
    );

    const handleMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = clampColumnWidth(startWidth + delta);
      updateColumnWidths((current) => ({
        ...current,
        [columnId]: nextWidth,
      }));
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [allColumns, effectiveColumnWidths, updateColumnWidths]);

  const handleRetry = () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    loadDetail();
  };

  const handleCreateEnrichment = useCallback(async ({
    label,
    prompt,
    fieldType,
  }) => {
    if (!label || !prompt || creatingEnrichment) return;
    setCreatingEnrichment(true);
    try {
      await addProspectingSavedListField(listId, {
        label,
        fieldType,
        kind: 'ai_enriched',
        prompt,
        runNow: true,
      });
      await loadDetail();
      setEnrichmentModalOpen(false);
      onReloadSavedLists?.();
      showSuccess('Enrichment column added');
    } catch (error) {
      showError(error.message || 'Could not add enrichment');
    } finally {
      setCreatingEnrichment(false);
    }
  }, [creatingEnrichment, listId, loadDetail, onReloadSavedLists, showError, showSuccess]);

  const handleRunEnrichment = useCallback(async (fieldId) => {
    if (!fieldId || runningEnrichmentFieldIds[fieldId]) return;
    setRunningEnrichmentFieldIds((current) => ({ ...current, [fieldId]: true }));
    try {
      await runProspectingSavedListEnrichment(listId, fieldId);
      await loadDetail();
      showSuccess('Enrichment started');
    } catch (error) {
      showError(error.message || 'Could not start enrichment');
    } finally {
      setRunningEnrichmentFieldIds((current) => ({ ...current, [fieldId]: false }));
    }
  }, [listId, loadDetail, runningEnrichmentFieldIds, showError, showSuccess]);

  const handleValueCommit = useCallback(
    async (recordId, fieldId, value) => {
      const cellKey = `${recordId}:${fieldId}`;
      setSavingCell(cellKey);
      try {
        const savedValue = await updateProspectingSavedListValue(
          listId,
          recordId,
          fieldId,
          value
        );
        setState((current) => ({
          ...current,
          detail: current.detail
            ? {
                ...current.detail,
                records: current.detail.records.map((record) => (
                  record.id === recordId
                    ? {
                        ...record,
                        manual_values: {
                          ...record.manual_values,
                          [fieldId]: savedValue.value,
                        },
                        field_values: {
                          ...(record.field_values || {}),
                          [fieldId]: {
                            field_id: fieldId,
                            value: savedValue.value,
                            status: savedValue.status || 'ok',
                            error: savedValue.error || null,
                            updated_by: savedValue.updated_by || 'user',
                            updated_at: savedValue.updated_at,
                            computed_at: savedValue.computed_at || null,
                          },
                        },
                      }
                    : record
                )),
              }
            : current.detail,
        }));
        onReloadSavedLists?.();
      } catch (error) {
        showError(error.message || 'Could not update cell');
      } finally {
        setSavingCell(null);
      }
    },
    [listId, onReloadSavedLists, showError]
  );

  const handleExport = async () => {
    if (!detail || exporting) return;
    setExporting(true);
    try {
      const blob = await loadProspectingSavedListCsv(listId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = safeFilename(listTitle);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error.message || 'Could not export saved list');
    } finally {
      setExporting(false);
    }
  };

  const renderBaseColumnCell = useCallback((column, record) => {
    if (column.id === 'base:name') {
      return <NameCell record={record} />;
    }

    if (column.id === 'base:company') {
      return <CompanyCell record={record} />;
    }

    const value = column.getValue ? column.getValue(record) : '';

    if (column.valueType === 'url') {
      return <UrlValueCell value={value} />;
    }

    if (column.valueType === 'availability') {
      return <AvailabilityCell value={value} />;
    }

    return <ReadOnlyCell value={value} />;
  }, []);

  const expandedRecord = useMemo(
    () => records.find((record) => record.id === expandedRecordId) || null,
    [expandedRecordId, records]
  );

  if (state.loading) {
    return (
      <StateMessage icon={Loader2} title="Loading saved list">
        <span className="inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {listTitle}
        </span>
      </StateMessage>
    );
  }

  if (state.error) {
    return (
      <StateMessage
        icon={AlertCircle}
        title="Saved list unavailable"
        action={(
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        )}
      >
        {state.error}
      </StateMessage>
    );
  }

  if (!detail) {
    return (
      <StateMessage icon={Table2} title="No saved list selected" />
    );
  }

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
      <div className="relative z-40 flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm">
        <div className="px-6 py-3.5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {/*
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
              <Table2 className="w-3 h-3" />
              Saved list
            </div>
            <h2 className="mt-1 text-base font-semibold text-gray-900 dark:text-white truncate">
              {listTitle}
            </h2>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {detail.record_count} {entityLabel} · {visibleSummary}
            </div>*/}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
              <ColumnVisibilityMenu
                baseColumns={baseColumns}
                manualColumns={manualColumns}
                columnVisibility={effectiveColumnVisibility}
                onToggle={toggleColumn}
                onShowAllBase={showAllBaseColumns}
                onResetDefaults={resetDefaultColumns}
              />
            <button
              type="button"
              onClick={() => setEnrichmentModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Add enrichment
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-brand-300 dark:hover:border-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              CSV
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-2">
        {visibleColumns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center dark:border-gray-700 dark:bg-gray-900">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">No columns visible</div>
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">Turn columns back on to resume editing this list.</div>
            <button
              type="button"
              onClick={resetDefaultColumns}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-brand-300 hover:text-brand-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-brand-700 dark:hover:text-brand-300"
            >
              Reset columns
            </button>
          </div>
        ) : (
          <div className="min-w-full overflow-hidden border border-gray-200 bg-white shadow-[0_1px_0_rgba(0,0,0,0.02)] dark:border-gray-700 dark:bg-gray-900">
            <div className="overflow-auto">
              <table className="w-max table-fixed border-separate border-spacing-0 text-left">
                <thead className="sticky top-0 z-20 bg-gray-50/95 dark:bg-gray-900">
                  <tr className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                    <th className="sticky left-0 z-40 w-10 min-w-[2.5rem] border-b border-r border-gray-200 bg-gray-50 px-1 py-2 text-center dark:border-gray-700 dark:bg-gray-900">
                      <span className="sr-only">Expand row</span>
                    </th>
                    <th className="sticky left-[2.5rem] z-40 w-10 min-w-[2.5rem] border-b border-r border-gray-200 bg-gray-50 px-1 py-2 text-center dark:border-gray-700 dark:bg-gray-900">
                      #
                    </th>
                    {visibleColumns.map((column, columnIndex) => {
                      const columnWidthPx = effectiveColumnWidths[column.id] || getDefaultColumnWidthPx(column);
                      const pinnedHeaderClass = columnIndex === 0
                        ? 'sticky left-[5rem] z-30 bg-gray-50/95 dark:bg-gray-900'
                        : '';
                      const fieldId = column.field?.id;
                      const latestJob = fieldId ? latestEnrichmentJobByField[fieldId] : null;
                      const enrichmentRunning = Boolean(
                        latestJob && (latestJob.status === 'queued' || latestJob.status === 'running')
                      );
                      const rerunBusy = Boolean(fieldId && runningEnrichmentFieldIds[fieldId]);
                      return (
                        <th
                          key={column.id}
                          style={{ width: `${columnWidthPx}px`, minWidth: `${columnWidthPx}px` }}
                          className={`relative ${pinnedHeaderClass} border-b border-r border-gray-200 px-2 py-2 dark:border-gray-700`}
                        >
                          {column.kind === 'manual' && column.isAiColumn ? (
                            <div className="flex items-center justify-between gap-2">
                              <span
                                title={column.prompt || undefined}
                                className="inline-flex min-w-0 items-center gap-1.5 truncate text-brand-600 dark:text-brand-300"
                              >
                                <Sparkles className="h-3 w-3" />
                                {column.label}
                              </span>
                              <button
                                type="button"
                                title={enrichmentRunning ? 'Enrichment running' : 'Run enrichment'}
                                onClick={() => handleRunEnrichment(fieldId)}
                                disabled={rerunBusy || enrichmentRunning}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-brand-300 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-500 dark:hover:border-brand-700 dark:hover:text-brand-300"
                              >
                                {rerunBusy || enrichmentRunning ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="block truncate pr-2">{column.label}</span>
                          )}
                          <button
                            type="button"
                            aria-label={`Resize ${column.label} column`}
                            title="Drag to resize column"
                            onMouseDown={(resizeEvent) => startColumnResize(resizeEvent, column.id)}
                            className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none opacity-0 transition-opacity hover:opacity-100 focus:opacity-100"
                          >
                            <span className="absolute right-0 top-1/2 h-5 -translate-y-1/2 border-r border-gray-300 dark:border-gray-600" />
                          </button>
                        </th>
                      );
                    })}
                    <th
                      style={{ width: '12.5rem', minWidth: '12.5rem' }}
                      className="border-b border-r border-gray-200 px-2 py-2 dark:border-gray-700"
                    >
                      <button
                        type="button"
                        onClick={() => setEnrichmentModalOpen(true)}
                        className="inline-flex h-7 w-full items-center justify-center rounded-md border border-dashed border-brand-300 bg-brand-50/70 px-2 text-[11px] font-semibold normal-case tracking-normal text-brand-700 transition-colors hover:border-brand-400 hover:bg-brand-100 hover:text-brand-800 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-300 dark:hover:border-brand-600 dark:hover:bg-brand-900/35"
                      >
                        + Add AI enrichment
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, index) => (
                    <tr
                      key={record.id}
                      className="group bg-white transition-colors hover:bg-gray-50/70 dark:bg-gray-900 dark:hover:bg-gray-800/40"
                    >
                      <td className="sticky left-0 z-30 w-10 min-w-[2.5rem] border-b border-r border-gray-200 bg-white px-1 py-1.5 text-center text-xs font-medium text-gray-400 transition-colors group-hover:bg-gray-50/70 dark:border-gray-700 dark:bg-gray-900 dark:group-hover:bg-gray-800/40">
                        <button
                          type="button"
                          onClick={() => setExpandedRecordId(record.id)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-gray-400 transition-colors hover:border-brand-200 hover:bg-brand-50 hover:text-brand-600 dark:text-gray-500 dark:hover:border-brand-800 dark:hover:bg-brand-900/20 dark:hover:text-brand-300"
                          title="Expand record"
                        >
                          <Maximize2 className="h-3 w-3" />
                        </button>
                      </td>
                      <td className="sticky left-[2.5rem] z-30 w-10 min-w-[2.5rem] border-b border-r border-gray-200 bg-white px-1 py-1.5 text-center text-xs font-medium text-gray-400 transition-colors group-hover:bg-gray-50/70 dark:border-gray-700 dark:bg-gray-900 dark:group-hover:bg-gray-800/40">
                        {index + 1}
                      </td>
                      {visibleColumns.map((column, columnIndex) => {
                        const pinnedBodyClass = columnIndex === 0
                          ? 'sticky left-[5rem] z-20 bg-white group-hover:bg-gray-50/70 dark:bg-gray-900 dark:group-hover:bg-gray-800/40'
                          : '';
                        if (column.kind === 'manual') {
                          const field = column.field;
                          const cellKey = `${record.id}:${field.id}`;
                          return (
                            <td
                              key={column.id}
                              style={{ width: `${effectiveColumnWidths[column.id]}px`, minWidth: `${effectiveColumnWidths[column.id]}px` }}
                              className={`${pinnedBodyClass} border-b border-r border-gray-200 px-1 py-1 align-top dark:border-gray-700`}
                            >
                              {column.isAiColumn ? (
                                <AiValueCell
                                  cell={record.field_values?.[field.id]}
                                />
                              ) : (
                                <EditableManualCell
                                  value={record.manual_values?.[field.id]}
                                  disabled={savingCell === cellKey}
                                  onCommit={(value) => handleValueCommit(record.id, field.id, value)}
                                />
                              )}
                            </td>
                          );
                        }

                        return (
                          <td
                            key={column.id}
                            style={{ width: `${effectiveColumnWidths[column.id]}px`, minWidth: `${effectiveColumnWidths[column.id]}px` }}
                            className={`${pinnedBodyClass} border-b border-r border-gray-200 px-2 py-1.5 align-top dark:border-gray-700`}
                          >
                            {renderBaseColumnCell(column, record)}
                          </td>
                        );
                      })}
                      <td
                        style={{ width: '12.5rem', minWidth: '12.5rem' }}
                        className="border-b border-r border-gray-200 px-2 py-1.5 align-top dark:border-gray-700"
                      >
                        <div
                          aria-hidden
                          className="h-7 rounded-md border border-dashed border-transparent"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {records.length === 0 && (
              <div className="py-16 text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 mb-3">
                  <X className="w-4 h-4 text-gray-400" />
                </div>
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Empty list
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <AddEnrichmentModal
        open={enrichmentModalOpen}
        entityType={entityType}
        creating={creatingEnrichment}
        onClose={() => setEnrichmentModalOpen(false)}
        onCreate={handleCreateEnrichment}
      />
      <RecordDetailModal
        open={Boolean(expandedRecord)}
        record={expandedRecord}
        columns={allColumns}
        onClose={() => setExpandedRecordId(null)}
      />
    </main>
  );
}
